import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { inputsLength } from '@/domains/auth/constants/validation';
import AuthEvents from '@/domains/auth/events';
import { AuthPageContainer } from '@/domains/auth/layouts/AuthPageContainer/AuthPageContainer';
import { getOtpConfigIn, otpSignIn } from '@/domains/auth/store/authSlice';
import {
  selectAuthOtpConfig,
  selectAuthOtpConfigIsLoading,
  selectAuthSignInIsLoading,
} from '@/domains/auth/store/selectors';
import { validatePassCode } from '@/constants/validation';
import { useEventEffect } from '@/hooks/useEventEffect';
import { RootPaths } from '@/routes/paths';
import { useDispatch, useSelector } from '@/store/hooks';
import { AdaptiveButton, Form, MuiOtpInputStyled, Options, Title } from './Otp.styles';
import { validationSchema } from './validationSchema';

const otpTitleTypeData: { [key: string]: string } = {
  Email: 'Please check your email and enter one time pass code below.',
  Sms: 'Please check your SMS and enter one time pass code below.',
};

const defaultValues = {
  otp: '',
};

type FormValues = typeof defaultValues;

export const Otp = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation() as Location<{ username: string }>;

  const otpConfigData = useSelector(selectAuthOtpConfig);
  const otpConfigIsLoading = useSelector(selectAuthOtpConfigIsLoading);
  const signInIsInProgress = useSelector(selectAuthSignInIsLoading);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    clearErrors,
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const otp = watch('otp');

  const handleBackClick = () => {
    navigate(RootPaths.SIGN_IN);
  };

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    dispatch(otpSignIn({ username: location.state.username, password: data.otp }));
  };

  useEffect(() => {
    clearErrors('otp');
  }, [otp, clearErrors]);

  useEffect(() => {
    if (!location.state?.username) navigate(RootPaths.SIGN_IN);
  }, [location.state?.username, navigate]);

  useEffect(() => {
    dispatch(getOtpConfigIn());
  }, []);

  useEventEffect(AuthEvents.RedirectToSignIn, () => navigate(RootPaths.SIGN_IN));

  useEventEffect(AuthEvents.RedirectToDefault, () => navigate(RootPaths.ROOT));

  useEventEffect(AuthEvents.RedirectToUpdatePassword, () => navigate(RootPaths.UPDATE_PASSWORD));

  return (
    <AuthPageContainer>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Title>
          <Typography data-testid="OptTitle" variant={isSmallScreen ? 'h5' : 'h2'}>
            The last step
          </Typography>
          <Typography variant={isSmallScreen ? 'body2' : 'body1'}>
            {otpConfigData && otpTitleTypeData[otpConfigData.otpMethod]}
          </Typography>
        </Title>
        <Controller
          name="otp"
          control={control}
          render={({ field }) => (
            <MuiOtpInputStyled
              validateChar={validatePassCode}
              error={!!errors.otp?.message}
              TextFieldsProps={(index) => ({
                inputProps: {
                  'aria-label': `Digit ${index + 1} of OTP`,
                  'data-testid': `Digit ${index + 1} of OTP`,
                },
              })}
              length={inputsLength.otp.min}
              {...field}
            />
          )}
        />
        <Options>
          <AdaptiveButton
            data-testid="OptSubmitButton"
            width="fluid"
            size="large"
            type="submit"
            disabled={!isValid || signInIsInProgress || otpConfigIsLoading}>
            SUBMIT
          </AdaptiveButton>
          <AdaptiveButton
            data-testid="OptBackButton"
            width="fluid"
            variant="secondary"
            size="large"
            onClick={handleBackClick}>
            BACK
          </AdaptiveButton>
        </Options>
      </Form>
    </AuthPageContainer>
  );
};
